import { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import formatNumber from 'src/utils/formatNumber';
import { StreamSection } from './StreamPageSections';
import type { LeadPackagePurchase } from 'src/services/LeadStreamsService/agent';

const tariffNames: Record<number, string> = { 50: 'Старт', 100: 'Про', 200: 'Бизнес' };
const purchaseCountLabel = (count: number) => {
    const noun = count % 10 === 1 && count % 100 !== 11 ? 'покупка'
        : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14)
            ? 'покупки' : 'покупок';
    return `${count} ${noun}`;
};

const money = (value: string | number) => {
    const amount = Number(value);
    const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency', currency: 'RUB',
        minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(Number.isFinite(rounded) ? rounded : 0);
};

export function purchasesForStream(purchases: LeadPackagePurchase[], streamId: number) {
    return purchases
        .filter((purchase) => (purchase.stream?.id || purchase.stream_id) === streamId)
        .sort((left, right) => Date.parse(right.paid_at || right.created_at) - Date.parse(left.paid_at || left.created_at));
}

export default function StreamPurchaseHistory({ purchases, loading, error }: {
    purchases: LeadPackagePurchase[];
    loading: boolean;
    error: boolean;
}) {
    const [showAll, setShowAll] = useState(false);
    const paidPurchases = purchases.filter((purchase) => purchase.status === 'paid' && Boolean(purchase.paid_at));
    return (
        <StreamSection id="purchases" title="История покупок" summary={paidPurchases.length ? purchaseCountLabel(paidPurchases.length) : undefined}>
            {loading ? <CircularProgress size={22} /> : error ? (
                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14 }}>Не удалось загрузить покупки.</Typography>
            ) : paidPurchases.length === 0 ? (
                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14 }}>Оплаченных покупок пока нет.</Typography>
            ) : (
                <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {paidPurchases.slice(0, showAll ? paidPurchases.length : 5).map((purchase) => {
                        const tariff = tariffNames[purchase.package_size];
                        const size = purchase.credited_package_size || purchase.package_size;
                        const title = purchase.purchase_type === 'package'
                            ? `${tariff ? `${tariff} · ` : ''}${formatNumber(size)} лидов`
                            : purchase.offer_name || 'Доступ к потоку';
                        return (
                            <Box component="li" key={purchase.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, py: 1.5, borderTop: '1px solid var(--lm-border)', '&:first-of-type': { borderTop: 0, pt: 0 } }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: 'var(--lm-text)', fontWeight: 700, fontSize: 14 }}>
                                        {title}
                                    </Typography>
                                    <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12.5, mt: 0.3 }}>
                                        {dayjs(purchase.paid_at).format('D MMMM YYYY')}
                                        {purchase.purchase_type === 'package' && Number(purchase.price_per_lead) > 0
                                            ? ` · ${money(purchase.price_per_lead)}/лид` : ''}
                                    </Typography>
                                </Box>
                                <Typography sx={{ color: 'var(--lm-text)', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {money(purchase.total_price)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
            {!loading && !error && paidPurchases.length > 5 && (
                <Button type="button" onClick={() => setShowAll(!showAll)} sx={{ mt: 1, px: 0, textTransform: 'none' }}>
                    {showAll ? 'Свернуть' : `Показать все ${paidPurchases.length}`}
                </Button>
            )}
        </StreamSection>
    );
}
